import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    Empty,
    message,
    Modal,
    Popconfirm,
    Row,
    Segmented,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    CheckCircleOutlined,
    HistoryOutlined,
    ReloadOutlined,
    RollbackOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { flightLogRecognitionApi } from './api';
import RecognitionRulesPanel from './RecognitionRulesPanel';

const { Text } = Typography;

// 比率字段 0~1 → 百分比文本；后端返回 null（暂无样本）时显示 '-'，不伪装成 0%
const formatRate = (v) => (v == null || Number.isNaN(Number(v)) ? '-' : `${(Number(v) * 100).toFixed(1)}%`);

const TAB_TO_DOMAIN = {
    'flight-log': 'flight_log',
    image: 'fuel_form',
    list: 'fuel_report',
};

const DOMAIN_OPTIONS = [
    { label: 'Flight Log 识别 (flight_log)', value: 'flight_log' },
    { label: '加油单图片识别 (fuel_form)', value: 'fuel_form' },
    { label: '航班加油明细 (fuel_report)', value: 'fuel_report' },
];

const MODEL_SOURCE_TAG = {
    db: { color: 'blue', label: '页面指定（免重启热生效）' },
    env: { color: 'gold', label: '环境变量 (.env)' },
    default: { color: 'default', label: '系统默认' },
};

const RecognitionPromptDrawer = ({ open, onClose, activeTab = 'flight-log', ruleSummary = null, onRulesChanged }) => {
    const [domain, setDomain] = useState(TAB_TO_DOMAIN[activeTab] || 'flight_log');
    const [innerTab, setInnerTab] = useState('monitor');
    const [loading, setLoading] = useState(false);
    const [rollingBack, setRollingBack] = useState(false);
    const [switchingModel, setSwitchingModel] = useState(false);
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        if (open) {
            setDomain(TAB_TO_DOMAIN[activeTab] || 'flight_log');
        }
    }, [open, activeTab]);

    const fetchVersions = useCallback(async (targetDomain = domain) => {
        setLoading(true);
        try {
            const res = await flightLogRecognitionApi.promptVersions(targetDomain);
            setPayload(res.data || res);
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message;
            message.error(`加载提示词监控数据失败: ${detail}`);
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [domain]);

    useEffect(() => {
        if (open && domain) {
            fetchVersions(domain);
        }
    }, [open, domain, fetchVersions]);

    const applyModelChange = async (nextModelId) => {
        setSwitchingModel(true);
        try {
            const res = await flightLogRecognitionApi.updatePromptModel(domain, {
                model_id: nextModelId,
            });
            const modelData = res.data || res;
            if (nextModelId) {
                message.success(`已切换识别模型为 ${modelData?.effective_model_id || nextModelId}，下一张识别单据起立即生效（无需重启服务）`);
            } else {
                message.success(`已恢复跟随默认配置（当前生效：${modelData?.effective_model_id || 'kimi-k2.6'}）`);
            }
            fetchVersions(domain);
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message;
            message.error(`切换识别模型失败: ${detail}`);
        } finally {
            setSwitchingModel(false);
        }
    };

    const handleModelChange = (nextVal) => {
        const nextModelId = nextVal || null;
        const domainLabel = DOMAIN_OPTIONS.find((o) => o.value === domain)?.label || domain;
        Modal.confirm({
            title: '确认切换视觉识别模型？',
            content: `「${domainLabel}」之后的所有新识别任务（含自动重试与重新识别）都将使用`
                + `${nextModelId ? ` ${nextModelId}` : '环境变量/系统默认模型'}。`
                + '模型不同会影响识别准确率与成本，切换会被记录到审计日志。',
            okText: '确认切换',
            cancelText: '取消',
            onOk: () => applyModelChange(nextModelId),
        });
    };

    const handleRollback = async () => {
        setRollingBack(true);
        try {
            const res = await flightLogRecognitionApi.rollbackPromptVersion(domain, {
                reason: '管理员于识别规则与版本监控抽屉执行一键回滚',
            });
            const data = res.data || res;
            message.success(`已回滚至版本 ${data?.active_version?.version_key || data?.active_version_key || ''}`);
            fetchVersions(domain);
            if (onRulesChanged) onRulesChanged();
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message;
            message.error(`回滚失败: ${detail}`);
        } finally {
            setRollingBack(false);
        }
    };

    const handleRulesChanged = () => {
        fetchVersions(domain);
        if (onRulesChanged) onRulesChanged();
    };

    const segmentedOptions = DOMAIN_OPTIONS.map((opt) => {
        const count = ruleSummary?.domains?.[opt.value]?.pending_count || 0;
        return {
            value: opt.value,
            label: (
                <Badge count={count} size="small" offset={[8, -2]}>
                    <span>{opt.label}</span>
                </Badge>
            ),
        };
    });

    const versions = payload?.versions || payload?.items || [];
    const supported = (payload?.supported === true || payload?.enabled === true) && Array.isArray(versions) && versions.length > 0;
    const activeVersion = payload?.active_version || versions.find((v) => v.is_active || v.status === 'active');
    const metrics = payload?.metrics || activeVersion?.stats || {};
    const autoAdopt = payload?.auto_adopt_evaluation || {};
    const modelConfig = payload?.model_config || {};
    const supportsModelSwitch = modelConfig?.supports_model_switch === true;
    const sourceMeta = MODEL_SOURCE_TAG[modelConfig?.model_source] || MODEL_SOURCE_TAG.default;

    const fallbackModelId = modelConfig?.env_model_id || modelConfig?.default_model_id || 'kimi-k2.6';
    const modelSelectOptions = [
        {
            value: '',
            label: `跟随系统默认 / 环境变量（${fallbackModelId}）`,
        },
        ...(Array.isArray(modelConfig?.available_models) ? modelConfig.available_models : []).map((m) => ({
            value: m.id,
            label: `${m.label || m.id} [${m.id}]`,
        })),
    ];

    const versionColumns = [
        {
            title: '版本号',
            dataIndex: 'version_key',
            width: 110,
            render: (v, row) => (
                <Space size={4}>
                    <Text strong>{v || `v${row.version}`}</Text>
                    {(row.is_active || row.status === 'active') && <Tag color="green">当前生效</Tag>}
                </Space>
            ),
        },
        {
            title: '内容摘要 (SHA-256)',
            dataIndex: 'content_sha256',
            width: 150,
            render: (v, row) => {
                const sha = v || row.sha256;
                return <Text code>{sha ? `${sha.slice(0, 12)}…` : '-'}</Text>;
            },
        },
        {
            title: '静态规则数',
            dataIndex: 'source_rule_ids',
            width: 100,
            render: (ids) => (Array.isArray(ids) ? ids.length : 0),
        },
        {
            title: '激活时间',
            dataIndex: 'activated_at',
            width: 170,
            render: (v) => (v ? String(v).slice(0, 19).replace('T', ' ') : '-'),
        },
        {
            title: '回滚记录',
            dataIndex: 'rollback_reason',
            ellipsis: true,
            render: (v) => v || '-',
        },
    ];

    const autoAdoptRows = Array.isArray(autoAdopt.fields)
        ? autoAdopt.fields.map((stat) => ({
            key: stat.field,
            ...stat,
        }))
        : Object.entries(autoAdopt.fields || {}).map(([field, stat]) => ({
            key: field,
            field,
            ...stat,
        }));

    const autoAdoptColumns = [
        {
            title: '字段',
            dataIndex: 'field',
            width: 160,
            render: (v, row) => (
                <Space size={4}>
                    <Text code>{v}</Text>
                    {row.is_key_field && <Tag color="gold">关键字段</Tag>}
                </Space>
            ),
        },
        {
            title: '样本数',
            dataIndex: 'total_samples',
            width: 90,
            render: (v, row) => v ?? row.sample_count ?? 0,
        },
        {
            title: '采纳率',
            dataIndex: 'adopt_rate',
            width: 100,
            render: (v, row) => `${((Number(v ?? row.adoption_rate) || 0) * 100).toFixed(1)}%`,
        },
        {
            title: '平均置信度',
            dataIndex: 'avg_confidence',
            width: 110,
            render: (v) => (v != null ? Number(v).toFixed(2) : '-'),
        },
        {
            title: '达标评估 (D19)',
            dataIndex: 'eligible',
            width: 120,
            render: (ok, row) => (
                (ok ?? row.meets_d19_threshold)
                    ? <Tag color="success">可开启自动采用</Tag>
                    : <Tag color="default">积累样本中</Tag>
            ),
        },
    ];

    return (
        <Drawer
            title={(
                <Space>
                    <HistoryOutlined />
                    <span>识别规则与版本监控</span>
                </Space>
            )}
            width={820}
            open={open}
            onClose={onClose}
            extra={(
                <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() => fetchVersions(domain)}
                >
                    刷新
                </Button>
            )}
            destroyOnHidden
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Segmented
                    block
                    options={segmentedOptions}
                    value={domain}
                    onChange={(val) => setDomain(val)}
                />

                <Tabs
                    activeKey={innerTab}
                    onChange={setInnerTab}
                    size="small"
                    items={[
                        { key: 'monitor', label: '版本监控' },
                        {
                            key: 'rules',
                            label: (
                                <Badge count={ruleSummary?.domains?.[domain]?.pending_count || 0} size="small" offset={[8, -2]}>
                                    <span>识别规则</span>
                                </Badge>
                            ),
                        },
                    ]}
                />

                {innerTab === 'rules' ? (
                    <RecognitionRulesPanel key={domain} domain={domain} onChanged={handleRulesChanged} />
                ) : (
                <Spin spinning={loading}>
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        {supportsModelSwitch && (
                            <Card
                                size="small"
                                title={(
                                    <Space>
                                        <ThunderboltOutlined style={{ color: '#1677ff' }} />
                                        <span>当前视觉识别模型（免重启热切换）</span>
                                    </Space>
                                )}
                                extra={<Tag color={sourceMeta.color}>{sourceMeta.label}</Tag>}
                            >
                                <Row gutter={[16, 12]} align="middle">
                                    <Col span={14}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                选择识别模型（切换后对下一张识别单据立即生效，无需改 .env 或重启服务）：
                                            </Text>
                                            <Select
                                                style={{ width: '100%' }}
                                                value={modelConfig?.preferred_model_id || ''}
                                                options={modelSelectOptions}
                                                loading={switchingModel}
                                                disabled={switchingModel}
                                                onChange={handleModelChange}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={10}>
                                        <Descriptions size="small" column={1}>
                                            <Descriptions.Item label="当前实际生效">
                                                <Space size={4}>
                                                    <Tag color="geekblue">{modelConfig?.effective_model_id || 'kimi-k2.6'}</Tag>
                                                    {modelConfig?.effective_provider && (
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            ({modelConfig.effective_provider})
                                                        </Text>
                                                    )}
                                                </Space>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="环境变量兜底">
                                                <Text code style={{ fontSize: 12 }}>
                                                    {modelConfig?.env_var_name || 'FLIGHT_LOG_VLM_MODEL'}={modelConfig?.env_model_id || '未设置'}
                                                </Text>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Col>
                                    {Array.isArray(modelConfig?.recent_changes) && modelConfig.recent_changes.length > 0 && (
                                        <Col span={24}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                最近切换：
                                                {modelConfig.recent_changes.slice(0, 3).map((c, idx) => (
                                                    <span key={`${c.created_at}-${idx}`} style={{ marginLeft: 8 }}>
                                                        {(c.created_at || '').slice(0, 19).replace('T', ' ')}
                                                        {' '}用户 {c.operator_id || '-'}：{c.from_model_id || '默认'} → {c.to_model_id || '默认'}
                                                        {idx < Math.min(2, modelConfig.recent_changes.length - 1) ? '；' : ''}
                                                    </span>
                                                ))}
                                            </Text>
                                        </Col>
                                    )}
                                </Row>
                            </Card>
                        )}

                        {!supported ? (
                            <Card size="small">
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={payload?.empty_reason || payload?.empty_message || '当前模块暂未启用版本化提示词监控'}
                                />
                            </Card>
                        ) : (
                            <>
                                <Card
                                    size="small"
                                    title={(
                                        <Space>
                                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                            <span>当前激活提示词版本</span>
                                        </Space>
                                    )}
                                    extra={versions.length > 1 && (
                                        <Popconfirm
                                            title="确定回滚到上一个提示词版本？"
                                            description="回滚后新发起的识别任务将立即使用上一版本提示词。"
                                            okText="确认回滚"
                                            cancelText="取消"
                                            onConfirm={handleRollback}
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                icon={<RollbackOutlined />}
                                                loading={rollingBack}
                                            >
                                                一键回滚上一版本
                                            </Button>
                                        </Popconfirm>
                                    )}
                                >
                                    <Descriptions size="small" column={2}>
                                        <Descriptions.Item label="版本号">
                                            <Tag color="green">{activeVersion?.version_key || (activeVersion?.version ? `v${activeVersion.version}` : '-')}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="SHA-256">
                                            <Text code>
                                                {(activeVersion?.content_sha256 || activeVersion?.sha256)
                                                    ? `${(activeVersion.content_sha256 || activeVersion.sha256).slice(0, 16)}…`
                                                    : '-'}
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="激活时间">
                                            {activeVersion?.activated_at ? String(activeVersion.activated_at).slice(0, 19).replace('T', ' ') : '-'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="关联静态规则" span={2}>
                                            {Array.isArray(activeVersion?.source_rules) && activeVersion.source_rules.length > 0 ? (
                                                <Space direction="vertical" size={2}>
                                                    <Text>{`共 ${activeVersion.source_rules.length} 条：`}</Text>
                                                    {activeVersion.source_rules.slice(0, 5).map((r) => (
                                                        <Text key={r.id} ellipsis style={{ maxWidth: 640, fontSize: 12 }}>
                                                            {`[${r.field ? `字段 ${r.field}` : '全字段'}] ${r.text || r.id}`}
                                                        </Text>
                                                    ))}
                                                    {activeVersion.source_rules.length > 5 && <Text type="secondary" style={{ fontSize: 12 }}>更多请在下方版本历史中展开查看</Text>}
                                                </Space>
                                            ) : '基础版本（无附加规则段）'}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                <Card size="small" title="线上运行与纠错监控（零额外 LLM 成本）">
                                    {metrics.sample_note && (
                                        <Alert
                                            type="info"
                                            showIcon
                                            style={{ marginBottom: 12 }}
                                            message={metrics.sample_note}
                                        />
                                    )}
                                    <Row gutter={16}>
                                        <Col span={6}>
                                            <Statistic title="总运行次数" value={metrics.total_runs ?? 0} />
                                        </Col>
                                        <Col span={6}>
                                            <Statistic title="成功次数" value={metrics.success_runs ?? metrics.succeeded_runs ?? 0} valueStyle={{ color: '#52c41a' }} />
                                        </Col>
                                        <Col span={6}>
                                            <Statistic title="平均耗时 (ms)" value={metrics.avg_latency_ms ?? metrics.avg_duration_ms ?? '-'} />
                                        </Col>
                                        <Col span={6}>
                                            <Statistic
                                                title="入池纠错样本"
                                                value={metrics.hint_eligible_corrections ?? metrics.correction_count ?? 0}
                                                suffix={`/ ${metrics.total_corrections ?? metrics.total_correction_events ?? 0}`}
                                            />
                                        </Col>
                                    </Row>
                                    <Row gutter={16} style={{ marginTop: 16 }}>
                                        <Col span={6}>
                                            <Tooltip title="1 − 纠偏率：成功识别中未被人工纠正模型错误的单据占比（勾选「原单据本身写错」的不扣减）">
                                                <Statistic title="线上准确率" value={formatRate(metrics.accuracy_rate)} />
                                            </Tooltip>
                                        </Col>
                                        <Col span={6}>
                                            <Tooltip title="成功识别中，审核时被人工纠正过模型错误的单据占比">
                                                <Statistic title="纠偏率" value={formatRate(metrics.correction_rate)} />
                                            </Tooltip>
                                        </Col>
                                        <Col span={6}>
                                            <Tooltip title="识别完成时即落入待复核状态（低置信度 / 多候选歧义等）的占比，按识别当时的状态固化，不随后续确认变化">
                                                <Statistic title="待复核率" value={formatRate(metrics.review_required_rate)} />
                                            </Tooltip>
                                        </Col>
                                        <Col span={6}>
                                            <Tooltip title="模型依据工具做出的自修正中，被审核人直接采纳的占比；暂无已裁决样本时显示 -">
                                                <Statistic title="模型修正采纳率" value={formatRate(metrics.suggestion_adoption_rate)} />
                                            </Tooltip>
                                        </Col>
                                    </Row>
                                </Card>

                                {domain !== 'fuel_form' && (
                                    <Card size="small" title="字段级自动采用达标评估 (D19)">
                                        {(autoAdopt.recommendation || autoAdopt.recommended_whitelist) && (
                                            <Alert
                                                type="info"
                                                showIcon
                                                style={{ marginBottom: 12 }}
                                                message={
                                                    autoAdopt.recommendation
                                                    || `建议开启自动采用白名单：${autoAdopt.recommended_whitelist}`
                                                }
                                            />
                                        )}
                                        <Table
                                            size="small"
                                            rowKey="key"
                                            pagination={false}
                                            columns={autoAdoptColumns}
                                            dataSource={autoAdoptRows}
                                            locale={{ emptyText: '暂无字段级预填评估样本' }}
                                        />
                                    </Card>
                                )}

                                <Card size="small" title="提示词版本历史">
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        pagination={false}
                                        columns={versionColumns}
                                        dataSource={versions}
                                        expandable={{
                                            expandedRowRender: (row) => (
                                                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                                    {Array.isArray(row.source_rules) && row.source_rules.length > 0 ? row.source_rules.map((r, idx) => (
                                                        <Text key={r.id} style={{ fontSize: 12 }}>
                                                            {`${idx + 1}. [${r.field ? `字段 ${r.field}` : '全字段'}] ${r.text || r.id}`}
                                                            {r.current_status && r.current_status !== 'adopted' && <Tag style={{ marginLeft: 6 }}>{`当前：${r.current_status}`}</Tag>}
                                                        </Text>
                                                    )) : <Text type="secondary" style={{ fontSize: 12 }}>该版本无附加规则段（纯净基础提示词）</Text>}
                                                    {row.content && (
                                                        <Typography.Paragraph
                                                            style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginBottom: 0 }}
                                                            ellipsis={{ rows: 3, expandable: true, symbol: '展开完整提示词正文' }}
                                                        >
                                                            {row.content}
                                                        </Typography.Paragraph>
                                                    )}
                                                </Space>
                                            ),
                                        }}
                                    />
                                </Card>
                            </>
                        )}
                    </Space>
                </Spin>
                )}
            </Space>
        </Drawer>
    );
};

export default RecognitionPromptDrawer;
